import { Controller } from '@hotwired/stimulus'
import { debounce } from 'lodash-es'

// Gates submit buttons on the form's state when data-form-enable-submit-when-value is set:
//   dirty   - something changed since the last submit
//   valid   - the form passes constraint validation
//   checked - at least one checkbox is checked
type EnableSubmitWhen = '' | 'dirty' | 'valid' | 'checked'

export default class extends Controller<HTMLFormElement> {
  static values = { enableSubmitWhen: { type: String, default: '' } }
  declare readonly enableSubmitWhenValue: EnableSubmitWhen

  private readonly events: string[] = ['input', 'change', 'lexxy:change', 'turbo:submit-start']

  connect (): void {
    for (const event of this.events) this.element.addEventListener(event, this)
    this.syncSubmitButtons()
  }

  disconnect (): void {
    for (const event of this.events) this.element.removeEventListener(event, this)
  }

  handleEvent (event: Event): void {
    switch (event.type) {
      case 'input':
      case 'change':
      case 'lexxy:change':
        this.sully()
        break
      case 'turbo:submit-start':
        this.submitWithDebounce.cancel()
        if (this.isCleansable(event)) this.cleanse()
        break
    }
  }

  submit (): void {
    this.element.requestSubmit()
  }

  submitWithDebounce = debounce(this.submit, 300)

  clear (): void {
    const clearableInputs = this.element.querySelectorAll('input:not([data-clear="false"]), textarea:not([data-clear="false"])')
    clearableInputs.forEach(el => { (el as HTMLInputElement | HTMLTextAreaElement).value = '' })
  }

  resetIfSuccess (e: CustomEvent): void {
    if (e.detail.success === true) {
      this.element.reset()
    }
  }

  sully (): void {
    this.dirty = true
    for (const button of this.submitButtons) button.setAttribute('aria-disabled', 'false')
    this.syncSubmitButtons()
  }

  cleanse (): void {
    this.dirty = false
    for (const button of this.submitButtons) button.setAttribute('aria-disabled', 'true')
    this.syncSubmitButtons()
  }

  get dirty (): boolean {
    return this.data.get('dirty') === 'true'
  }

  private set dirty (value: boolean) {
    this.data.set('dirty', value.toString())
  }

  private syncSubmitButtons (): void {
    if (this.enableSubmitWhenValue === '') return

    const enabled = this.submitAllowed()
    this.submitButtons.forEach((button) => { (button as HTMLButtonElement).disabled = !enabled })
  }

  private submitAllowed (): boolean {
    switch (this.enableSubmitWhenValue) {
      case 'dirty':
        return this.dirty
      case 'valid':
        return this.element.checkValidity()
      case 'checked':
        return this.element.querySelector('input[type="checkbox"]:checked') !== null
      default:
        return true
    }
  }

  private get submitButtons (): Element[] {
    return Array.from(this.element.elements).filter((element: Element) => (
      element.getAttribute('type') === 'submit'
    ))
  }

  private isCleansable (event: Event): boolean {
    const button = (event as CustomEvent).detail.formSubmission.submitter as HTMLElement
    return button?.dataset?.nocleanse === undefined
  }
}
